import { Dispatch, SetStateAction } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { UpcomingOrdersMeetingService } from "../../../modules/nonAdminUser/reports/upcomingOrders/meetingService/meetingServiceApi"
import { CreateOrderDialogFlow, CreateOrderDialogMode, CreateOrderDialogType } from "./createOrderTypes"
import { ConfigQuery } from "./createOrderApi"
import { OutlookBookingSuccessArgs } from "../../../modules/order/pages/outlookCreateOrderTypes"
import { UserWeekMenu } from "../../../modules/nonAdminUser/reports/home/weekMenuApi"
import { UserDetails } from "../../../modules/nonAdminUser/nonAdminUserApi"
import { CreateOrderDialogController } from "./CreateOrderDialogController"
import { OrderOnBehalfFlow } from "./orderOnBehalf/OrderOnBehalfFlow"
import { OnBehalfCompletedDialog } from "./hooks/useCreateOrderUser"
import { CreateOrderType } from "./helpers"

type CreateOrderDialogProps = Readonly<{
    open: boolean
    onClose: (val: boolean) => void
    activeOrderTab?: CreateOrderType
    dialogType?: CreateOrderDialogType
    orderId?: number
    mode: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
    isOnBehalfCompleted: OnBehalfCompletedDialog
    setIsOnBehalfCompleted: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
}>

export function CreateOrderDialog({
    open,
    onClose,
    activeOrderTab,
    dialogType = CreateOrderDialogType.ORDER,
    orderId,
    mode = "create",
    flowConfig,
    handleOutlookBookingCompleted,
    isOnBehalfCompleted,
    setIsOnBehalfCompleted,
}: CreateOrderDialogProps) {
    const queryClient = useQueryClient()

    const handleOnComplete = (id: number, name: string, companyId: number) => {
        setIsOnBehalfCompleted((prev) => {
            return { ...prev, show: false, userId: id, displayName: name, companyId }
        })
    }

    const handleClose = () => {
        onClose(false)
        queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.MEETING_SERVICE_LIST],
        })
        queryClient.invalidateQueries({
            queryKey: [UserDetails.USER_CALENDAR_EVENTS],
        })
        queryClient.invalidateQueries({
            queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY],
        })
        queryClient.invalidateQueries({
            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
        })
        queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE],
            refetchType: "all", // adding because react query does not refetch query.
        })
    }

    return (
        <>
            {!isOnBehalfCompleted.show && (
                <CreateOrderDialogController
                    open={open}
                    orderId={orderId}
                    mode={mode}
                    flowConfig={flowConfig}
                    handleClose={handleClose}
                    activeOrderTab={activeOrderTab}
                    dialogType={dialogType}
                    isOnBehalfCompleted={isOnBehalfCompleted}
                    setIsOnBehalfCompleted={setIsOnBehalfCompleted}
                    handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                />
            )}
            {isOnBehalfCompleted.show && (
                <OrderOnBehalfFlow
                    userType={isOnBehalfCompleted.userType}
                    onComplete={handleOnComplete}
                    handleCloseParentDialog={handleClose}
                />
            )}
        </>
    )
}
